type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | null | undefined | false;

/** Tiny element builder. Text always goes in through `textContent`, so no
 *  reviewer's comment can inject markup into the overlay. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else node.setAttribute(key, String(value));
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/**
 * An inline SVG icon.
 *
 * Built node by node rather than from a markup string, so the no-innerHTML rule
 * above holds here too. Inline rather than a font or a file because the widget
 * must never fetch an external resource — a strict CSP on a client's page blocks
 * that silently, and the icon would simply be missing there.
 */
export function icon(paths: string[], size = 14): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of paths) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}

/** Sliders rather than a funnel. A funnel at 14px is a triangle, and reads as a
 *  dropdown caret — which is what it was being mistaken for. */
export const FILTER_ICON = [
  'M21 4H14',
  'M10 4H3',
  'M21 12H12',
  'M8 12H3',
  'M21 20H16',
  'M12 20H3',
  'M14 2v4',
  'M8 10v4',
  'M16 18v4',
];

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function shortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
